import React, { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Sidebar } from "../components/Sidebar";
import { useHotels } from "../components/Home/hooks/useHotels";
import HotelsTable from "../components/Home/HotelsTable";
import HomePageHeader from "../components/Home/HomePageHeader";
import HotelsSummary from "../components/Home/HotelsSummary";
import DescriptionModal from "../components/Home/DescriptionModal";
import EditHotelModal from "../components/Home/EditHotelModal";
import { ErrorState } from "../components/UI";
import { AiOutlineSearch, AiOutlineClose } from "react-icons/ai";

function Home() {
  const navigate = useNavigate();
  const { hotels, loading, loadingStats, statsPendingIds, error, searchTerm, setSearchTerm, updateHotelStatus, generateSnapshot, fetchHotels, sortState, handleSort, updateFeatureStatus, updateTag } = useHotels();
  const [descriptionModal, setDescriptionModal] = useState({
    isOpen: false,
    hotel: null,
  });
  const [editHotelModal, setEditHotelModal] = useState({
    isOpen: false,
    hotel: null,
  });

  // Handlers stables : ils descendent jusqu'aux lignes, mémoïsées. Une nouvelle référence
  // à chaque render suffirait à re-rendre les 83 lignes.
  const handleEditDescription = useCallback((hotel) => {
    setDescriptionModal({ isOpen: true, hotel });
  }, []);

  const handleEditHotel = useCallback((hotel) => {
    setEditHotelModal({ isOpen: true, hotel });
  }, []);

  // `fetchHotels` ne prend aucun argument : l'ancien `debouncedSearchTerm` n'était jamais
  // renvoyé par le hook et valait donc toujours undefined.
  const handleDescriptionSuccess = useCallback(() => {
    fetchHotels();
  }, [fetchHotels]);

  const handleEditHotelSuccess = useCallback(() => {
    fetchHotels();
  }, [fetchHotels]);

  const handleAddHotel = useCallback(() => {
    navigate("/addHotel");
  }, [navigate]);

  const closeDescriptionModal = useCallback(() => setDescriptionModal({ isOpen: false, hotel: null }), []);
  const closeEditHotelModal = useCallback(() => setEditHotelModal({ isOpen: false, hotel: null }), []);

  // Aucun early return : l'en-tête et la barre de recherche restent affichés pendant le
  // chargement comme en cas d'erreur, pour que la page soit navigable immédiatement.
  return (
    <div className="flex h-screen">
      <Sidebar />
      <div className="flex-1 p-4 overflow-auto">
        <HomePageHeader onAddHotel={handleAddHotel} />

        {/* Barre de recherche - toujours visible */}
        <div className="mb-4 ml-6">
          <div className="relative inline-block" style={{ minWidth: "250px" }}>
            <AiOutlineSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="Rechercher un hôtel..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-gray-400"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm("")}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                aria-label="Effacer la recherche"
              >
                <AiOutlineClose size={18} />
              </button>
            )}
          </div>
        </div>

        {/* Indicateurs de chargement, discrets et non bloquants */}
        {loading && <div className="mb-2 ml-6 text-sm text-gray-500">Chargement des hôtels...</div>}
        {!loading && loadingStats && <div className="mb-2 ml-6 text-sm text-gray-500">Chargement des statistiques...</div>}

        {error && (
          <div className="ml-6">
            <ErrorState error={error} embedded />
          </div>
        )}

        <HotelsSummary totalHotels={hotels.length} />

        {/* Message si aucun résultat de recherche */}
        {!loading && searchTerm.trim() && hotels.length === 0 && (
          <div className="bg-white shadow-md rounded-lg p-8 text-center ml-6">
            <div className="text-black text-lg">Aucun hôtel trouvé pour "{searchTerm}"</div>
          </div>
        )}

        {!error && !(searchTerm.trim() && hotels.length === 0) && (
          <HotelsTable hotels={hotels} onUpdateStatus={updateHotelStatus} onGenerateSnapshot={generateSnapshot} onEditDescription={handleEditDescription} onEditHotel={handleEditHotel} sortState={sortState} onSort={handleSort} statsPendingIds={statsPendingIds} onUpdateFeatureStatus={updateFeatureStatus} onUpdateTag={updateTag} isGeneralList={true} loading={loading} />
        )}

        <DescriptionModal isOpen={descriptionModal.isOpen} onClose={closeDescriptionModal} hotel={descriptionModal.hotel} onSuccess={handleDescriptionSuccess} />
        <EditHotelModal isOpen={editHotelModal.isOpen} onClose={closeEditHotelModal} hotel={editHotelModal.hotel} onSuccess={handleEditHotelSuccess} />
      </div>
    </div>
  );
}

export default Home;
